import React, { Fragment, useEffect, useCallback, useState, startTransition } from 'react';
import PropTypes from 'prop-types';
import { get, noop } from 'lodash-es';
import { Button } from 'reactstrap';
import {
  PAYMENT_INPUT_PROPS,
  TOOLTIPS,
  DOWN_PAYMENT_NAME,
  AMOUNT_OWED_ON_TRADE_IN_LABEL,
  VALUE_OF_YOUR_TRADE_IN_LABEL,
  APPLIED_TRADE_IN_LABEL,
} from 'site-modules/calculators/constants/calculators';
import { MORE_INFO } from 'client/site-modules/shared/constants/appraisal/appraisal';
import { URL_PATHS } from 'site-modules/shared/constants/appraisal/paths';
import { PaymentInput } from 'site-modules/shared/components/calculator/payment-input/payment-input';
import { VehicleSelectorView } from 'site-modules/calculators/components/vehicle-selector-view/vehicle-selector-view';
import { CalculatorRowItem } from 'site-modules/calculators/components/calculator-row-item/calculator-row-item';
import { TradeInAppraiserDrawer } from 'site-modules/shared/components/appraisal/trade-in-appraiser/trade-in-appraiser-drawer';
import { Link } from 'site-modules/shared/components/link/link';

const { TRADE_IN, TRADE_IN_AFFORDABILITY, AMOUNT_OWED_TRADE_IN, CASH_DOWN_PAYMENT, CAP_COST_REDUCTION } = TOOLTIPS;
const APPLIED_TRADE_IN_TOOLTIP = {
  text: MORE_INFO.APPLIED_TRADE_IN,
  placement: 'bottom',
};
const focusOnTradeIn = () => {
  const tradeInInput = document.getElementById('tradeIn');

  if (tradeInInput) {
    tradeInInput.focus();
  }
};

/**
 * Renders trade-in and down payment step.
 * @returns {ReactElement}
 */

export const CalculatorStepTradeIn = ({
  downPayment = 0,
  capCostReduction = 0,
  tradeIn = 0,
  owedTradeIn = 0,
  appliedTradeIn = null,
  onInputChange,
  onKeyPress,
  onRemoveStyle = noop,
  onTradeInUpdate = noop,
  onVehicleUpdate = noop,
  style = null,
  withCapCostReduction = false,
  withCashDownPayment = true,
  buttonOptions = {},
  tradeInDrawerData = null,
  renderTradeInDrawer = true,
  vin = '',
  TitleWrapper = Fragment,
  overrideWrapperClass = false,
  isAffordabilityCalculator = false,
}) => {
  const btnValue = get(buttonOptions, 'value', 'Value your trade-in');
  const btnSize = get(buttonOptions, 'size', 'sm');
  const [updatedTradIn, setUpdatedTradIn] = useState(tradeIn);
  const [isAppraisalDrawerOpen, setIsAppraisalDrawerOpen] = useState(false);

  const toggleAppraisalDrawer = useCallback(() => {
    startTransition(() => {
      setIsAppraisalDrawerOpen(state => !state);
    });
  }, []);

  useEffect(() => {
    if (!isAffordabilityCalculator && !isAppraisalDrawerOpen && updatedTradIn !== tradeIn) {
      setTimeout(() => focusOnTradeIn(), 700);
      startTransition(() => {
        setUpdatedTradIn(tradeIn);
      });
    }
    // eslint-disable-next-line
  }, [tradeIn, isAppraisalDrawerOpen]);

  const getTradeInBtn = useCallback(() => {
    if (!renderTradeInDrawer) {
      return (
        <Button
          tag={Link}
          to={`${URL_PATHS.APPRAISAL_LANDING}${vin ? `?vin=${vin}` : ''}`}
          target="_blank"
          size={btnSize}
          color="outline-blue-40"
          data-tracking-id="select_appraise"
          className="text-transform-none"
        >
          {btnValue}
        </Button>
      );
    }

    return !style ? (
      <Button
        onClick={toggleAppraisalDrawer}
        size={btnSize}
        color="outline-primary-b"
        data-tracking-id="select_appraise"
        className="text-transform-none"
      >
        {btnValue}
      </Button>
    ) : null;
  }, [btnSize, btnValue, renderTradeInDrawer, style, toggleAppraisalDrawer, vin]);

  return (
    <Fragment>
      <CalculatorRowItem
        title="Enter the details of your Trade-in to receive the most accurate calculation."
        TitleWrapper={TitleWrapper}
      >
        {getTradeInBtn()}
      </CalculatorRowItem>
      <VehicleSelectorView style={style} onChange={toggleAppraisalDrawer} onDelete={onRemoveStyle} />
      <CalculatorRowItem
        title={VALUE_OF_YOUR_TRADE_IN_LABEL}
        tooltip={isAffordabilityCalculator ? TRADE_IN_AFFORDABILITY : TRADE_IN}
        TitleWrapper={TitleWrapper}
      >
        <PaymentInput
          name="tradeIn"
          key={updatedTradIn}
          value={updatedTradIn}
          onKeyPress={onKeyPress}
          onBlur={onInputChange}
          isCurrency
          {...PAYMENT_INPUT_PROPS}
          inputAriaLabel="Trade In"
        />
      </CalculatorRowItem>
      <CalculatorRowItem
        title={AMOUNT_OWED_ON_TRADE_IN_LABEL}
        tooltip={AMOUNT_OWED_TRADE_IN}
        TitleWrapper={TitleWrapper}
      >
        <PaymentInput
          name="owedTradeIn"
          key={owedTradeIn}
          value={owedTradeIn}
          onKeyPress={onKeyPress}
          onBlur={onInputChange}
          isCurrency
          {...PAYMENT_INPUT_PROPS}
          inputAriaLabel={AMOUNT_OWED_ON_TRADE_IN_LABEL}
        />
      </CalculatorRowItem>
      {!!appliedTradeIn && (
        <CalculatorRowItem
          title={APPLIED_TRADE_IN_LABEL}
          tooltip={APPLIED_TRADE_IN_TOOLTIP}
          TitleWrapper={TitleWrapper}
        >
          <PaymentInput
            name="appliedTradeIn"
            key={appliedTradeIn}
            value={appliedTradeIn}
            onKeyPress={onKeyPress}
            onBlur={onInputChange}
            isCurrency
            {...PAYMENT_INPUT_PROPS}
            inputAriaLabel={APPLIED_TRADE_IN_LABEL}
          />
        </CalculatorRowItem>
      )}
      {withCashDownPayment && (
        <CalculatorRowItem
          title="Cash Down Payment"
          tooltip={CASH_DOWN_PAYMENT}
          TitleWrapper={TitleWrapper}
          overrideWrapperClass={overrideWrapperClass}
        >
          <PaymentInput
            name={DOWN_PAYMENT_NAME}
            key={downPayment}
            value={downPayment}
            onKeyPress={onKeyPress}
            onBlur={onInputChange}
            isCurrency
            {...PAYMENT_INPUT_PROPS}
            inputAriaLabel="Cash Down Payment"
          />
        </CalculatorRowItem>
      )}
      {withCapCostReduction && (
        <CalculatorRowItem title="Cap Cost Reduction" tooltip={CAP_COST_REDUCTION} TitleWrapper={TitleWrapper}>
          <PaymentInput
            name="capCostReduction"
            key={capCostReduction}
            value={capCostReduction}
            onKeyPress={onKeyPress}
            onBlur={onInputChange}
            isCurrency
            {...PAYMENT_INPUT_PROPS}
            inputAriaLabel="Cap Cost Reduction"
          />
        </CalculatorRowItem>
      )}
      {renderTradeInDrawer && (
        <TradeInAppraiserDrawer
          isOpen={isAppraisalDrawerOpen}
          onToggle={toggleAppraisalDrawer}
          onTradeInUpdate={onTradeInUpdate}
          onVehicleUpdate={onVehicleUpdate}
          trackingParent="calculator-appraisal-drawer"
          {...tradeInDrawerData}
        />
      )}
    </Fragment>
  );
};
CalculatorStepTradeIn.propTypes = {
  onInputChange: PropTypes.func.isRequired,
  onKeyPress: PropTypes.func.isRequired,
  onRemoveStyle: PropTypes.func,
  onTradeInUpdate: PropTypes.func,
  onVehicleUpdate: PropTypes.func,
  downPayment: PropTypes.number,
  capCostReduction: PropTypes.number,
  tradeIn: PropTypes.number,
  owedTradeIn: PropTypes.number,
  appliedTradeIn: PropTypes.number,
  style: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    year: PropTypes.number,
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  withCapCostReduction: PropTypes.bool,
  withCashDownPayment: PropTypes.bool,
  buttonOptions: PropTypes.shape({}),
  tradeInDrawerData: PropTypes.shape({
    isPurchase: PropTypes.bool,
    sellingPrice: PropTypes.number,
    msrp: PropTypes.number,
    residualValue: PropTypes.number,
  }),
  renderTradeInDrawer: PropTypes.bool,
  vin: PropTypes.string,
  TitleWrapper: PropTypes.node,
  overrideWrapperClass: PropTypes.bool,
  isAffordabilityCalculator: PropTypes.bool,
};
